import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import useAxios from "../../utils/useAxios";

const INITIAL_PREVIEW_ROWS_DISPLAYED = 300;

const STATUS_DISPLAY = {
  error: { label: "Error", color: "error.main" },
  duplicate: { label: "Duplicate", color: "warning.main" },
  updated: { label: "Will update", color: "info.main" },
  created: { label: "Will create", color: "success.main" },
};

const getResultsArray = (data) => {
  const normalized = Array.isArray(data) ? data : data?.results || [];
  return Array.isArray(normalized) ? normalized : [];
};

const creatableFilterOptions = (options, params) => {
  const filtered = options.filter((option) =>
    option.name.toLowerCase().includes(params.inputValue.toLowerCase()),
  );
  if (params.inputValue.trim() !== "" && filtered.length === 0) {
    filtered.push({
      id: "__new__",
      name: `Add "${params.inputValue.trim()}"`,
      inputValue: params.inputValue.trim(),
    });
  }
  return filtered;
};

// Seeds the Autocomplete with the value already sitting in the cell (the
// invalid text from the spreadsheet), so "Add "<value>"" is selectable the
// moment the dropdown opens instead of requiring the user to retype what
// they already entered. `creatableFilterOptions` only synthesizes an "Add"
// entry from whatever MUI currently considers the live search text
// (`params.inputValue`) - an interaction test proved that text isn't
// reliably in sync with the pre-filled `value`/`inputValue` the moment the
// dropdown is opened (an internal MUI timing detail, not something worth
// chasing further), so the very first "Add" entry is injected directly
// into `options` instead, independent of that live text - and, while the
// field still holds that original unresolved value untouched, it's the
// *only* option shown, so the fix is a single obvious click rather than
// buried in a long unrelated list. Clearing the box (or typing something
// else) drops it and falls back to the full list / `creatableFilterOptions`'s
// normal live "Add" computation, exactly like every other creatable field.
// A plain function invoked during render
// (renderFieldCell) can't hold this state itself, hence its own component
// here.
const LookupCreateCell = ({
  row,
  fieldKey,
  options,
  disabled,
  onFieldEdit,
  onCreateOption,
  showError = true,
  onBlur,
}) => {
  const currentText = row[fieldKey] || "";
  const [value, setValue] = useState(currentText);

  const augmentedOptions =
    currentText && value === currentText
      ? [{ id: "__new__", name: `Add "${currentText}"`, inputValue: currentText }]
      : options;

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={augmentedOptions}
      value={value}
      inputValue={value}
      onInputChange={(_, newInputValue) => setValue(newInputValue)}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      disabled={disabled}
      filterOptions={creatableFilterOptions}
      onChange={async (_, selected) => {
        if (selected == null) {
          setValue("");
          return;
        }
        if (typeof selected === "string") {
          setValue(selected);
          return;
        }
        if (selected.id === "__new__") {
          const created = await onCreateOption(selected.inputValue);
          if (created) {
            setValue(created.name);
            onFieldEdit(row.row, fieldKey, created.name);
          }
          return;
        }
        setValue(selected.name);
        onFieldEdit(row.row, fieldKey, selected.name);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          error={showError}
          helperText={showError ? row.message : undefined}
          onBlur={onBlur}
          autoFocus
        />
      )}
    />
  );
};

/**
 * Config-driven editable preview table, shared by every Tier-style bulk
 * upload shell (TierCsvUpload/EquipmentCsvUpload/InstrumentCsvUpload) -
 * extracted out of TierCsvUpload.jsx so the row/fields/Status/delete table
 * and its create-if-missing/search "fix" controls are written once, not
 * once per domain.
 *
 * `columns`: [{key, label}, ...] - every field to show, in order.
 * Every cell, on every row regardless of bucket (created/duplicate/
 * updated/error), is editable - the user can fix or tweak anything, not
 * just what the validator flagged. Cells stay plain read-only text until
 * clicked, then swap in place for their control; clicking/tabbing away
 * reverts back to plain text (see renderFieldCell/editingCells below).
 * `fieldControls[key]` picks which control a field uses:
 *   - {type: "lookup-create", optionsEndpoint, createEndpoint, optionsParams?}
 *     a search + "Add "..."" create-if-missing Autocomplete (e.g. Asset
 *     Type/Instrument Type/Communication Protocol).
 *   - {type: "lookup-search", optionsEndpoint, optionsParams?, getOptionLabel?, getValue?}
 *     a search-only Autocomplete against an existing list (e.g. PLC/P&ID/
 *     IO Panel - things this flow never creates), single value.
 *   - {type: "lookup-search-multi", optionsEndpoint, optionsParams?, getOptionLabel?, getValue?}
 *     same search-only lookup, but multi-select (e.g. Related To - a Tier
 *     can relate to more than one Equipment). The cell's value is a
 *     comma-separated string of `getValue(option)` results, matching what
 *     the spreadsheet column itself accepts.
 *   - omitted (or {type: "text"}): a plain editable TextField, committed
 *     on blur.
 * A cell only skips the click-to-edit gate and stays permanently in its
 * editable control when it's the exact field an error row's validator
 * flagged (red-bordered, with the error message) - every other cell renders the same
 * control neutrally, pre-filled with its current value.
 */
const BulkImportPreviewTable = ({
  preview,
  columns,
  fieldControls = {},
  onFieldEdit,
  onRemoveRow,
  revalidating = false,
  uploading = false,
}) => {
  const api = useAxios();
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [optionsByField, setOptionsByField] = useState({});
  const [showAllRows, setShowAllRows] = useState(false);
  const [activeErrorRow, setActiveErrorRow] = useState(null);
  // Cells the user has clicked into to edit (a Set of "row-key" strings) -
  // every cell starts as plain, read-only text and only becomes an
  // editable control once clicked, then reverts back to plain text once
  // the user clicks/tabs away. The one exception is a row's actual
  // erroring field, which stays permanently editable (see
  // renderFieldCell) since it needs fixing regardless of a click.
  const [editingCells, setEditingCells] = useState(() => new Set());
  const rowRefs = useRef({});

  const cellKey = (row, key) => `${row.row}-${key}`;
  const startEditing = (row, key) => {
    setEditingCells((prev) => new Set(prev).add(cellKey(row, key)));
  };
  const stopEditing = (row, key) => {
    setEditingCells((prev) => {
      if (!prev.has(cellKey(row, key))) return prev;
      const next = new Set(prev);
      next.delete(cellKey(row, key));
      return next;
    });
  };

  const createdRows = preview?.created_rows ?? [];
  const duplicateRows = preview?.duplicate_rows ?? [];
  const updatedRows = preview?.updated_rows ?? [];
  const rowErrors = preview?.row_errors ?? [];

  // One combined, row-number-ordered list driving a single table - each
  // row tagged with which bucket it landed in, so a cell can tell whether
  // it should render as plain text or as an editable "fix" control. A
  // "duplicate" row matches an existing asset with identical values (a
  // true no-op, skipped); an "updated" row also matched one, but the
  // spreadsheet's values differ from what's stored, so it'll be patched
  // onto that existing record instead - detected automatically server-side,
  // not something the user has to trigger.
  const allRows = [
    ...createdRows.map((row) => ({ ...row, bucket: "created" })),
    ...duplicateRows.map((row) => ({ ...row, bucket: "duplicate" })),
    ...updatedRows.map((row) => ({ ...row, bucket: "updated" })),
    ...rowErrors.map((row) => ({ ...row, bucket: "error" })),
  ].sort((a, b) => a.row - b.row);

  const errorRows = useMemo(
    () => allRows.filter((row) => row.bucket === "error"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preview],
  );

  // Don't make the user hunt for errors: as soon as there's at least one,
  // jump straight to it. Once the active one gets fixed (a preview refresh
  // where it no longer appears in errorRows), automatically advance to the
  // next remaining error - repeating until none are left, since a "fix" is
  // just another preview refresh that runs this same effect again.
  useEffect(() => {
    if (errorRows.length === 0) {
      setActiveErrorRow(null);
      return;
    }

    setActiveErrorRow((current) => {
      if (current != null && errorRows.some((row) => row.row === current)) {
        return current;
      }
      return errorRows[0].row;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorRows]);

  // Jumping to an error that's past the initial page (only reachable when
  // there are more than INITIAL_PREVIEW_ROWS_DISPLAYED rows and the user
  // hasn't already expanded the table) has to reveal it first - flip
  // showAllRows and wait for the re-render that puts the row in the DOM
  // before trying to scroll to it, rather than scrolling to a ref that
  // doesn't exist yet.
  useEffect(() => {
    if (activeErrorRow == null) return;

    if (!showAllRows) {
      const isBeyondInitialPage =
        allRows.findIndex((row) => row.row === activeErrorRow) >=
        INITIAL_PREVIEW_ROWS_DISPLAYED;
      if (isBeyondInitialPage) {
        setShowAllRows(true);
        return;
      }
    }

    // scrollIntoView isn't implemented in jsdom (the test environment) and
    // may be missing in some embedded webviews - guard the call itself, not
    // just the element lookup, so its absence is a silent no-op rather than
    // a thrown error.
    rowRefs.current[activeErrorRow]?.scrollIntoView?.({
      behavior: "smooth",
      block: "center",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeErrorRow, showAllRows]);

  const goToNextError = () => {
    if (errorRows.length === 0) return;
    const currentIndex = errorRows.findIndex((row) => row.row === activeErrorRow);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % errorRows.length;
    setActiveErrorRow(errorRows[nextIndex].row);
  };

  // Lookup-dropdown option lists, fetched once the preview actually has
  // rows to show - every row is editable now, so a lookup-type cell (e.g.
  // Related To, PLC, Asset Type) needs its options loaded to render its
  // already-resolved value as selected instead of appearing blank - every
  // configured lookup field is fetched together, in one pass, the first
  // time any row appears.
  useEffect(() => {
    const needsOptions = allRows.length > 0;
    if (optionsLoaded || !needsOptions) return;
    setOptionsLoaded(true);

    const lookupEntries = Object.entries(fieldControls).filter(
      ([, control]) =>
        control &&
        (control.type === "lookup-create" ||
          control.type === "lookup-search" ||
          control.type === "lookup-search-multi"),
    );

    if (lookupEntries.length === 0) return;

    Promise.all(
      lookupEntries.map(([, control]) =>
        api.get(control.optionsEndpoint, { params: control.optionsParams }),
      ),
    )
      .then((responses) => {
        const next = {};
        lookupEntries.forEach(([key], index) => {
          next[key] = getResultsArray(responses[index].data);
        });
        setOptionsByField((prev) => ({ ...prev, ...next }));
      })
      .catch((err) => console.error("Error fetching fix-dropdown options:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, optionsLoaded]);

  // A new file/preview session (preview reset to null by the hook) means
  // any previously-fetched option lists/flags are stale for it too.
  useEffect(() => {
    if (!preview) {
      setOptionsLoaded(false);
      setOptionsByField({});
      setShowAllRows(false);
      setEditingCells(new Set());
    }
  }, [preview]);

  const createOption = async (key, control, name) => {
    try {
      const response = await api.post(control.createEndpoint, { name });
      const created = response.data;
      setOptionsByField((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), created],
      }));
      return created;
    } catch (err) {
      console.error(`Error creating option for ${key}:`, err);
      return null;
    }
  };

  // Renders one row's cell for `key`. Every field, on every row, CAN be
  // edited - but stays plain, read-only text until the user clicks it, so
  // the table doesn't turn into a wall of input boxes for rows that don't
  // need any attention. Clicking a cell swaps it in place for its control
  // (lookup-create/lookup-search/lookup-search-multi/plain TextField),
  // pre-filled with the current value; clicking/tabbing away (blur)
  // reverts it back to plain text showing whatever the value now is. The
  // one exception is a row's actual erroring field, which skips all of
  // that and stays permanently in its editable control - red-bordered,
  // with the error message as helperText - since it needs fixing
  // regardless of a click.
  const renderFieldCell = (row, key) => {
    const showError = row.bucket === "error" && row.field === key;
    const disabled = revalidating || uploading;
    const isEditing = showError || editingCells.has(cellKey(row, key));

    if (!isEditing) {
      return (
        <TableCell
          key={key}
          onClick={disabled ? undefined : () => startEditing(row, key)}
          sx={{
            cursor: disabled ? "default" : "pointer",
            "&:hover": disabled ? undefined : { bgcolor: "action.hover" },
          }}
        >
          {row[key] || "-"}
        </TableCell>
      );
    }

    const control = fieldControls[key] || { type: "text" };
    const options = optionsByField[key] || [];
    const onBlur = () => stopEditing(row, key);

    if (control.type === "lookup-create") {
      return (
        <TableCell key={key} sx={{ minWidth: 200 }}>
          <LookupCreateCell
            key={`${row.row}-${key}-${row[key]}`}
            row={row}
            fieldKey={key}
            options={options}
            disabled={disabled}
            showError={showError}
            onFieldEdit={onFieldEdit}
            onCreateOption={(name) => createOption(key, control, name)}
            onBlur={onBlur}
          />
        </TableCell>
      );
    }

    if (control.type === "lookup-search") {
      const getOptionLabel = control.getOptionLabel || ((option) => option.name || "");
      const getValue = control.getValue || ((option) => option.name);
      // Controlled, seeded from the row's current value - a row already
      // has a real, resolved value here once it's the one being edited,
      // which an uncontrolled Autocomplete would otherwise always render
      // blank on mount regardless. An error row's value is the invalid/
      // unresolved text, which by construction won't match any real
      // option, so this still starts blank there too.
      const currentValue = row[key] || "";
      const selectedOption =
        options.find((option) => getValue(option) === currentValue) || null;

      return (
        <TableCell key={key} sx={{ minWidth: 200 }}>
          <Autocomplete
            size="small"
            options={options}
            value={selectedOption}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={disabled}
            onChange={(_, selected) => {
              if (!selected) return;
              onFieldEdit(row.row, key, getValue(selected));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={showError}
                helperText={showError ? row.message : undefined}
                onBlur={onBlur}
                autoFocus
              />
            )}
          />
        </TableCell>
      );
    }

    if (control.type === "lookup-search-multi") {
      const getOptionLabel = control.getOptionLabel || ((option) => option.name || "");
      const getValue = control.getValue || ((option) => option.name);
      // The cell's value is a comma-separated string (the same format the
      // spreadsheet column itself accepts) - split it back into tokens and
      // resolve each against the loaded options so a row's already-
      // assigned relations show as chips, not blank, exactly like the
      // single-value lookup-search cell above. A token that doesn't
      // resolve (e.g. an error row's invalid text) is silently dropped
      // from the chip list rather than shown unresolved.
      const currentTokens = (row[key] || "")
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean);
      const selectedOptions = currentTokens
        .map((token) => options.find((option) => getValue(option) === token))
        .filter(Boolean);

      return (
        <TableCell key={key} sx={{ minWidth: 240 }}>
          <Autocomplete
            multiple
            size="small"
            options={options}
            value={selectedOptions}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={disabled}
            onChange={(_, selected) => {
              onFieldEdit(row.row, key, selected.map(getValue).join(", "));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={showError}
                helperText={showError ? row.message : undefined}
                onBlur={onBlur}
                autoFocus
              />
            )}
          />
        </TableCell>
      );
    }

    return (
      <TableCell key={key} sx={{ minWidth: 160 }}>
        <TextField
          key={`${row.row}-${key}-${row[key]}`}
          size="small"
          defaultValue={row[key] || ""}
          disabled={disabled}
          error={showError}
          helperText={showError ? row.message : undefined}
          autoFocus
          onBlur={(event) => {
            const value = event.target.value.trim();
            if (value && value !== row[key]) {
              onFieldEdit(row.row, key, value);
            }
            onBlur();
          }}
        />
      </TableCell>
    );
  };

  if (!preview) return null;

  const visibleRows = showAllRows
    ? allRows
    : allRows.slice(0, INITIAL_PREVIEW_ROWS_DISPLAYED);

  return (
    <Box>
      <Typography variant="body2">
        Will create: {preview?.summary?.created ?? 0}
      </Typography>
      <Typography variant="body2">
        Duplicates to skip: {preview?.summary?.duplicates_skipped ?? 0}
      </Typography>
      <Typography variant="body2">
        Will update: {preview?.summary?.updated ?? 0}
      </Typography>
      <Typography variant="body2" sx={{ mb: rowErrors.length ? 1.5 : 0 }}>
        Rows with errors: {preview?.summary?.errors ?? 0}
      </Typography>

      {allRows.length > 0 && (
        <Box>
          <TableContainer sx={{ maxHeight: 420, overflowY: "auto", overflowX: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key}>{column.label}</TableCell>
                  ))}
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, index) => (
                  <TableRow
                    key={`${row.row}-${index}`}
                    ref={(el) => {
                      rowRefs.current[row.row] = el;
                    }}
                    data-active-error={row.row === activeErrorRow ? "true" : undefined}
                    sx={
                      row.row === activeErrorRow
                        ? {
                            outline: "2px solid",
                            outlineColor: "error.main",
                            outlineOffset: -2,
                          }
                        : undefined
                    }
                  >
                    <TableCell>{row.row}</TableCell>
                    {columns.map((column) => renderFieldCell(row, column.key))}
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: STATUS_DISPLAY[row.bucket].color,
                        }}
                      >
                        {STATUS_DISPLAY[row.bucket].label}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Remove this row from the upload">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={revalidating || uploading}
                            onClick={() => onRemoveRow(row.row)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!showAllRows && allRows.length > INITIAL_PREVIEW_ROWS_DISPLAYED && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Showing the first {INITIAL_PREVIEW_ROWS_DISPLAYED} of {allRows.length} rows.{" "}
              <Link component="button" type="button" onClick={() => setShowAllRows(true)}>
                Show all {allRows.length} rows
              </Link>
            </Typography>
          )}
        </Box>
      )}

      {errorRows.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
            {errorRows.length} row{errorRows.length === 1 ? "" : "s"}{" "}
            {errorRows.length === 1 ? "needs" : "need"} fixing before uploading.
          </Typography>
          <Button size="small" onClick={goToNextError}>
            Next error
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BulkImportPreviewTable;
